import { Helmet } from 'react-helmet';
import AboutUsImg from "../assets/about-us/about_us.webp";
import AboutLogo from "../assets/about-us/about.svg";
import Header from "../components/Header";
import WhyChoose from "../components/Home/WhyChoose";
import Footer from "../components/Footer";
import Counter from "../components/AboutUs/Counter";
import Faq from "../components/AboutUs/Faq";
import Breadcrumbs from '../components/BreadcrumbsComponent';
import { HelmetProvider } from 'react-helmet-async';
import { useEffect } from 'react';
import LogoLoop from '../components/LogoLoop';

const metadata = {
    title: "About Us | Your Travel Companion | Halo Holidays",
    description: "Learn more about Halo Holidays, our passion for travel, and our commitment to creating unforgettable experiences for our customers.",
};

const AboutUs = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <title>{metadata.title}</title>
                    <meta name="description" content={metadata.description} />
                </Helmet>
            </HelmetProvider>
            <Header />
            <section>
                <div className="relative h-[30vh] md:h-[50vh] mt-16">
                    <img
                        src={AboutUsImg}
                        alt="Background"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-opacity-50 flex items-center justify-center">
                        <div className="text-center p-4">
                            <h1 className="text-white text-2xl md:text-4xl font-bold">
                                A Splash of Adventure, a Burst of Joy
                            </h1>
                            <p className="text-white mt-2 text-base md:text-3xl">
                                Welcome to Our World of Travel
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            <div className="container mx-auto p-4">
                <div>
                    <Breadcrumbs />
                </div>

                <section className="mt-8 mb-8">
                    <div className="flex w-full bg-[#f9f9f9] p-2 rounded-sm items-center flex-col md:flex-row"> {/* Added bg-gray-100 for a light gray background */}
                        <div className="w-full md:w-4/12"> {/* Added full width for consistency */}
                            <div className="w-full p-4 flex justify-center md:-mt-3">
                                <img src={AboutLogo} alt="About Halo Holidays" className="w-7/12 h-auto object-contain" />
                            </div>
                            <h2 className="text-2xl font-bold text-center">Halo Holidays - <span className='text-halo-pink'>Here For You</span></h2>
                            <p className="mt-3 text-center text-gray-700 text-sm md:mb-4">
                                Escape the ordinary, embrace the extraordinary: Live experiences that redefine travel
                            </p>
                            <hr className="md:hidden mb-3 mt-6" />
                        </div>
                        <div className="benefits-divider w-1/12 bg-gray-300 min-h-full"></div> {/* Ensured divider reaches full height */}
                        <div className="benefits-details w-full md:w-7/12 p-4"> {/* Added padding for better content spacing */}
                            <p className="text-sm text-black mb-4 md:mt-4">
                                Craft unforgettable memories, not just holidays. Since 2018, we've ignited souls with journeys that blend seasoned wisdom and exhilarating discovery.
                                <br /><br />
                                We're not just an agency, we're your trusted travel companion. Explore a kaleidoscope of cultures with our global reach in the <span className='text-halo-pink'>UK</span>, <span className='text-halo-pink'>USA</span>, and <span className='text-halo-pink'>Sri Lanka</span>. Your faceted adventure awaits.
                                <br /><br />
                                Travel is a transformative odyssey, not a checklist. Forge connections, immerse yourself in breathtaking landscapes, and return with etched memories. Our passionate experts curate itineraries that resonate with your unique desires.
                                <br /><br />
                                Let Halo Holidays be your trusted guide and friend. Create memories that illuminate your life's journey. Ready for a splash of adventure and a burst of joy? We're <strong className='text-halo-pink'>here for you</strong>, every step of the way. Welcome to our world!
                            </p>
                        </div>

                    </div>
                </section>

                <section className="max-h-640 md:min-h-0 lg:min-h-0 my-8">
                    <h2 className="text-3xl font-semibold items-center justify-center flex mt-10">More reasons to choose us</h2>
                    <WhyChoose />
                    <hr className="mb-10 mt-10" />
                </section>

                <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 my-12 p-6">
                    <div className="flex flex-col items-center justify-center text-center space-y-6 -mt-10">
                        <h2 className="text-3xl font-extrabold text-gray-800">British Travel Award Winner</h2>
                        <img
                            src="/general/BTA.png"
                            alt="British Travel Awards"
                            className="max-w-full md:h-[17rem] h-[12rem]"
                            loading="lazy"
                        />
                    </div>

                    <div>
                        <h2 className="text-xl font-extrabold text-gray-800 text-center mb-8 -mt-4">As Seen In</h2>
                        <LogoLoop />
                        <div className="text-center flex flex-col items-center justify-center mt-10">
                            <p className="text-gray-700 text-sm leading-relaxed">
                                We are thrilled to announce that we, <span className='text-[#d7006a]'>Halo Holidays</span>, has been honored with <strong>Gold</strong> at the <strong>British Travel Awards for South East Asia!</strong> This prestigious recognition is a testament to our commitment to excellence and the trust you, our valued customers, have placed in us.
                            </p>
                            <p className="text-gray-700 text-sm leading-relaxed">
                                A heartfelt thank you to everyone who supported us on this incredible journey. Your loyalty and encouragement inspire us to continue delivering unforgettable travel experiences.
                            </p>
                            <p className="text-gray-700 text-sm leading-relaxed">
                                Here’s to many more adventures together! ✈️🌏
                            </p>
                        </div>
                    </div>
                </section>
                <hr className="mb-4 mt-4" />

                <section className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] md:p-6">
                    {/* Left Column */}
                    <div className="flex flex-col w-full">
                        <Faq />
                    </div>

                    {/* Right Column */}
                    <div className="flex items-center justify-center w-full">
                        <Counter />
                    </div>
                </section>

                <Footer />

            </div>
        </>
    );
};

export default AboutUs;